import {
    ArrowRightIcon,
    CheckIcon,
    CircleSubtractIcon,
    DurationIcon,
    EquipmentIcon,
    UserChangedIcon,
} from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { isArray } from "lodash";
import React, { ReactNode } from "react";
import { Audit, GuestStatus, Purchase, PurchaseItem } from "types";
import { NoTranslate } from "../../../Common/NoTranslate";
import { TimelineItem } from "../TimelineItem";

export interface GuestStatusChangedItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

/** This tile is to show status other than "Checked-in", CheckIn status would be handled by {@link GuestCheckInItem} with `checkInCount` */
export const GuestStatusChangedItem = ({ purchase, item, audit, showDate = false }: GuestStatusChangedItemProps) => {
    const match = getStatus(item, audit);
    if (!GuestStatusChangedItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    let allChanged = false;
    if (match.guests.length === item.guests.length) {
        allChanged = match.guests.every((guest: any) => guest.guestStatus?.new === item.guests[0].guestStatus);
    }

    if (allChanged) {
        const guest: any = Object.values(match.guests)[0];
        const [from, to] = [guest.guestStatus?.old, guest.guestStatus?.new];
        if (to === GuestStatus.ARRIVED && from !== GuestStatus.RETURNED) {
            /** It's a case where guest is checked in, but the "Checked-In" tile would be displayed by other component, no need to continue; */
            return null;
        }

        // All guests are in the same status because guest level check-in is not enabled
        return <AllGuestsStatus from={from} to={to} audit={audit} showDate={showDate} />;
    }

    return (
        <>
            {Object.values(match.guests).map((matchedGuest: any) => {
                const guest = item.guests.find((guest) => guest.id === matchedGuest?.id);
                const [from, to] = [matchedGuest.guestStatus?.old, matchedGuest.guestStatus?.new];
                if (to === GuestStatus.ARRIVED && from !== GuestStatus.RETURNED) {
                    return null;
                }

                return (
                    guest && (
                        <SingleGuestStatus
                            key={guest.id}
                            from={from}
                            to={to}
                            audit={audit}
                            guest={guest}
                            matchedGuest={matchedGuest}
                            showDate={showDate}
                        />
                    )
                );
            })}
        </>
    );
};

interface GuestStatusTimelineProps {
    to: string;
    audit: Audit;
    showDate: boolean;
    children: ReactNode;
}

const GuestStatusTimelineItem = ({ to, audit, showDate, children }: GuestStatusTimelineProps) => {
    return (
        <TimelineItem
            title={dataMap[to]?.title ?? "Guest Status Changed"}
            audit={audit}
            icon={dataMap[to]?.icon ?? <CheckIcon />}
            color={dataMap[to]?.color ?? "primary"}
            showDate={showDate}
        >
            {children}
        </TimelineItem>
    );
};

interface SingleGuestStatusProps {
    from: string;
    to: string;
    audit: Audit;
    guest: any;
    matchedGuest: any;
    showDate: boolean;
}

const SingleGuestStatus = ({ from, to, audit, guest, matchedGuest, showDate }: SingleGuestStatusProps) => {
    return (
        <GuestStatusTimelineItem to={to} audit={audit} showDate={showDate}>
            {to === GuestStatus.NO_SHOW ? (
                <span>
                    <NoTranslate>{guest?.name ?? `Guest ${matchedGuest.id}`}</NoTranslate> did not check-in for the
                    experience
                </span>
            ) : (
                <span>
                    <NoTranslate as="div" className="mb-1 text-gray-darker">
                        {guest?.name ?? `Guest ${matchedGuest.id}`}
                    </NoTranslate>
                    <div className="flex items-center space-x-2">
                        <Badge color={dataMap[from]?.color} size="medium" icon={dataMap[from]?.badge ?? null}>
                            {dataMap[from]?.value ?? "N/A"}
                        </Badge>
                        <ArrowRightIcon />
                        <Badge
                            color={dataMap[to]?.color ?? "secondary"}
                            size="medium"
                            icon={dataMap[to]?.badge ?? null}
                        >
                            {dataMap[to]?.value ?? "N/A"}
                        </Badge>
                    </div>
                </span>
            )}
        </GuestStatusTimelineItem>
    );
};

interface AllGuestsStatusProps {
    from: string;
    to: string;
    audit: Audit;
    showDate: boolean;
}

const AllGuestsStatus = ({ from, to, audit, showDate }: AllGuestsStatusProps) => {
    return (
        <GuestStatusTimelineItem to={to} audit={audit} showDate={showDate}>
            <div className="space-x-2">
                {to === GuestStatus.NO_SHOW ? (
                    <span>The customer did not check-in for the experience</span>
                ) : (
                    <div className="flex items-center space-x-2">
                        <Badge color={dataMap[from]?.color} size="medium" icon={dataMap[from]?.badge ?? null}>
                            {dataMap[from]?.value ?? "N/A"}
                        </Badge>
                        <ArrowRightIcon />
                        <Badge
                            color={dataMap[to]?.color ?? "secondary"}
                            size="medium"
                            icon={dataMap[to]?.badge ?? null}
                        >
                            {dataMap[to]?.value ?? "N/A"}
                        </Badge>
                    </div>
                )}
            </div>
        </GuestStatusTimelineItem>
    );
};

GuestStatusChangedItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return !!getStatus(item, audit);
};

const getStatus = (item: PurchaseItem, audit: Audit) => {
    if (audit.data?.items) {
        return (
            isArray(audit.data?.items) &&
            audit.data.items.find((auditItem: any) => {
                if (!auditItem.guests) {
                    return;
                }

                const guests: any = Object.values(auditItem.guests) ?? [];
                return auditItem?.id === item?.id && guests[0]?.guestStatus;
            })
        );
    }
};

const dataMap: { [key: string]: any } = {
    [GuestStatus.PENDING]: {
        title: "Guest Status Changed",
        value: "Pending",
        color: "warning",
        icon: <UserChangedIcon />,
        badge: <DurationIcon />,
    },
    [GuestStatus.ARRIVED]: {
        title: "Check-In",
        value: "Checked-In",
        color: "success",
        icon: <CheckIcon />,
        badge: <CheckIcon />,
    },
    [GuestStatus.NO_SHOW]: {
        title: "No Show",
        value: "No Show",
        color: "danger",
        icon: <CircleSubtractIcon />,
        badge: <CircleSubtractIcon />,
    },
    [GuestStatus.RETURNED]: {
        title: "Return Equipment",
        value: "Equipment Returned",
        color: "primary",
        icon: <EquipmentIcon />,
        badge: <EquipmentIcon />,
    },
};
